import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/pages/About";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { adminApi } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Trash2, Ticket, ArrowLeft, Copy, Plus, Check } from "lucide-react";

const AdminPromoCodes = () => {
  const navigate = useNavigate();
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [copied, setCopied] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ percent_off: 10, description: "", new_users_only: true, max_redemptions: "" });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.listPromoCodes();
      setCodes(data.promo_codes || []);
    } catch {
      toast.error("Could not load promotion codes");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (c, active) => {
    setSavingId(c.id);
    try {
      await adminApi.updatePromoCode(c.id, { active });
      setCodes((list) => list.map((x) => (x.id === c.id ? { ...x, active } : x)));
      toast.success(active ? "Code activated" : "Code deactivated");
    } catch {
      toast.error("Could not update code");
    } finally { setSavingId(null); }
  };

  const remove = async (c) => {
    setSavingId(c.id);
    try {
      await adminApi.deletePromoCode(c.id);
      setCodes((list) => list.filter((x) => x.id !== c.id));
      toast.success("Code deleted");
    } catch {
      toast.error("Could not delete code");
    } finally { setSavingId(null); }
  };

  const copy = async (code) => {
    try { await navigator.clipboard.writeText(code); setCopied(code); setTimeout(() => setCopied(null), 1500); } catch {}
  };

  const create = async () => {
    setCreating(true);
    try {
      const payload = {
        percent_off: Number(form.percent_off),
        description: form.description || undefined,
        new_users_only: form.new_users_only,
        max_redemptions: form.max_redemptions ? Number(form.max_redemptions) : null,
      };
      const { data } = await adminApi.createPromoCode(payload);
      toast.success(`Created code ${data.code}`);
      setForm({ percent_off: 10, description: "", new_users_only: true, max_redemptions: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create code");
    } finally { setCreating(false); }
  };

  return (
    <div>
      <PageHeader eyebrow="Admin" title="Promotion Codes"
        subtitle="Create and manage new-user promotion codes. Share a code with a client so they can enter it at checkout for a discount." />
      <section className="wechr-section">
        <div className="wechr-container space-y-6">
          <Button variant="outline" onClick={() => navigate("/admin")} data-testid="promo-back-button"><ArrowLeft className="mr-2 h-4 w-4" /> Back to dashboard</Button>

          {/* Create */}
          <Card>
            <CardContent className="p-6">
              <div className="mb-4 flex items-center gap-2"><Plus className="h-5 w-5 text-wgreen-700" /><h2 className="font-heading text-lg font-semibold text-navy-900">Create a new code</h2></div>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <label className="text-xs font-medium text-navy-800">Discount</label>
                  <Select value={String(form.percent_off)} onValueChange={(v) => setForm({ ...form, percent_off: v })}>
                    <SelectTrigger className="mt-1" data-testid="promo-percent-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{[10,20,30,40,50,60,70,80,90,100].map((p) => <SelectItem key={p} value={String(p)}>{p}%</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="lg:col-span-2">
                  <label className="text-xs font-medium text-navy-800">Description (optional)</label>
                  <Input className="mt-1" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. Founding client discount" data-testid="promo-desc-input" />
                </div>
                <div>
                  <label className="text-xs font-medium text-navy-800">Max redemptions (optional)</label>
                  <Input className="mt-1" type="number" min="1" value={form.max_redemptions} onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })} placeholder="Unlimited" data-testid="promo-max-input" />
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between gap-4">
                <label className="flex items-center gap-2 text-sm text-navy-800">
                  <Switch checked={form.new_users_only} onCheckedChange={(v) => setForm({ ...form, new_users_only: v })} data-testid="promo-newuser-switch" />
                  New users only (no prior paid certification)
                </label>
                <Button className="btn-green" onClick={create} disabled={creating} data-testid="promo-create-button">
                  {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />} Generate code
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* List */}
          <Card>
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-wgreen-600" /></div>
              ) : codes.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-16 text-center"><Ticket className="h-8 w-8 text-[var(--wechr-muted-text)]" /><p className="text-sm text-[var(--wechr-muted-text)]">No promotion codes yet.</p></div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead><TableHead>Discount</TableHead><TableHead>Scope</TableHead>
                      <TableHead>Redeemed</TableHead><TableHead>Description</TableHead><TableHead>Active</TableHead><TableHead className="text-right">Delete</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {codes.map((c) => (
                      <TableRow key={c.id} data-testid="promo-row">
                        <TableCell>
                          <button onClick={() => copy(c.code)} className="inline-flex items-center gap-2 rounded-md bg-[var(--wechr-subtle)] px-2.5 py-1 font-mono text-sm font-semibold text-navy-900 transition-colors hover:bg-wgreen-100" data-testid={`promo-copy-${c.code}`} title="Copy code">
                            {c.code} {copied === c.code ? <Check className="h-3.5 w-3.5 text-wgreen-700" /> : <Copy className="h-3.5 w-3.5 text-[var(--wechr-muted-text)]" />}
                          </button>
                        </TableCell>
                        <TableCell><span className="rounded-full bg-wgreen-100 px-2.5 py-1 text-xs font-semibold text-wgreen-700">{c.percent_off}% off</span></TableCell>
                        <TableCell className="text-xs text-[var(--wechr-muted-text)]">{c.new_users_only ? "New users only" : "All users"}</TableCell>
                        <TableCell className="text-sm text-navy-800">{c.times_redeemed || 0}{c.max_redemptions ? ` / ${c.max_redemptions}` : ""}</TableCell>
                        <TableCell className="max-w-[220px] truncate text-sm text-[var(--wechr-muted-text)]">{c.description}</TableCell>
                        <TableCell>
                          <Switch checked={c.active} disabled={savingId === c.id} onCheckedChange={(v) => toggleActive(c, v)} data-testid={`promo-active-${c.code}`} />
                        </TableCell>
                        <TableCell className="text-right">
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="outline" size="sm" disabled={savingId === c.id} data-testid={`promo-delete-${c.code}`}><Trash2 className="h-4 w-4 text-[#B42318]" /></Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete promotion code?</AlertDialogTitle>
                                <AlertDialogDescription>This will permanently delete <span className="font-mono font-semibold">{c.code}</span>. Anyone who was given this code will no longer be able to use it.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={() => remove(c)} className="bg-[#B42318] hover:bg-[#9A1D14]">Delete</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default AdminPromoCodes;
